<template>
  <div
    v-if="exercise"
    :id="exercise.objectID"
    class="md:flex-row md:space-x-12 relative flex flex-col p-4 mt-4 bg-white shadow-xl"
  >
    <div class="md:flex-shrink-0 md:w-1/2 md:static relative w-full">
      <ExerciseMedia
        :video16-to9-url="exercise.video16_9Url"
        :video4-to5-url="exercise.video4_5Url"
        :video-youtube-id="exercise.videoYoutubeId"
        :mp4-url="exercise.mp4Url"
        :gif-url="exercise.gifUrl"
        :png-url="exercise.pngUrl"
        :ratio="2 / 3"
        :width="624"
        :exercise="exercise"
        crop="fill"
      />
      <LikeBtn
        v-if="accountEnabled"
        :handle="exercise.objectID"
        :exercise="exercise"
        class="md:bottom-auto md:top-0 absolute bottom-0 right-0 m-4"
        type="exercise"
      />
    </div>
    <div class="text md:grow md:w-auto md:p-4 w-full pt-4">
      <h3 class="fluid-xl leading-tight font-medium" v-html="exercise.title"></h3>
      <div class="min-h-[5em] relative mt-4">
        <div class="text-copy fluid-lg font-light leading-normal" v-html="exercise.shortDescription"></div>
        <Expandable v-if="exercise.longDescription" :open="active">
          <div class="text-copy fluid-lg font-light leading-normal" v-html="exercise.longDescription"></div>
        </Expandable>
        <button
          v-if="exercise.longDescription"
          class="text-br-green focus:outline-none my-4 font-light cursor-pointer"
          data-test-id="toggle-read-more"
          @click="active = !active"
        >
          <span class="font-semibold">{{ active ? '-' : '+' }}</span>
          {{ active ? `${$t('showLess')}` : `${$t('readMore')}` }}
        </button>
      </div>
      <div v-if="exercise" class="flex flex-row flex-wrap w-full mt-4">
        <ExerciseSpec class="w-1/2" :label="$t(`exercises.product`)">
          <template slot="default"><CircleIcon class="h-6" /></template>
          <template slot="value">
            <div v-if="productHasTool">
              <nuxt-link
                v-for="product in products"
                :key="product.handle"
                class="product-link"
                :to="$localeRootPath(`/products/${product.handle}`)"
                >{{ product.title }}
              </nuxt-link>
            </div>
            <div v-else>
              <p class="product-link">{{ productNoToolTitle }}</p>
            </div>
          </template>
        </ExerciseSpec>
        <ExerciseSpec
          class="w-1/2"
          data-testid="practice"
          :label="exercise.practiceLabel"
          :value="exercise.practiceValue"
        >
          <PracticeColorIcon class="h-6" />
        </ExerciseSpec>
        <ExerciseSpec class="w-1/2" :label="$t(`exercises.bodyParts`)" :value="bodyParts">
          <BodyPartsIcon class="h-6" />
        </ExerciseSpec>
        <ExerciseSpec class="w-1/2" :label="$t(`exercises.trainingGoals`)" :value="trainingGoals">
          <TrainingGoalsColorIcon class="h-6" />
        </ExerciseSpec>
      </div>
    </div>
  </div>
</template>

<script>
import ExerciseSpec from '@/components/exercises/ExerciseSpec';
import CircleIcon from '@/components/icons/CircleIcon';
import TrainingGoalsColorIcon from '@/components/icons/TrainingGoalsColorIcon';
import PracticeColorIcon from '@/components/icons/PracticeColorIcon';
import BodyPartsIcon from '@/components/icons/BodyPartsIcon';
import LikeBtn from '@/components/common/LikeBtn';
import ExerciseMedia from '@/components/exercises/ExerciseMedia';
import Expandable from '@/components/common/Expandable';

export default {
  name: 'ExerciseCard',
  components: {
    Expandable,
    LikeBtn,
    CircleIcon,
    TrainingGoalsColorIcon,
    ExerciseSpec,
    ExerciseMedia,
    PracticeColorIcon,
    BodyPartsIcon,
  },
  props: {
    exercise: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      active: false,
    };
  },
  computed: {
    products() {
      return this.exercise?.products;
    },
    bodyParts() {
      return this.exercise?.bodyParts?.map((e) => e.title).join(', ') || '';
    },
    trainingGoals() {
      return this.exercise?.trainingGoals?.join(', ') || '';
    },
    accountEnabled() {
      return this.$config.auth.enabled;
    },
    productHasTool() {
      return this.products?.length && this.products[0].handle !== 'no-tool';
    },
    productNoToolTitle() {
      return this.products?.length && this.products[0].title;
    },
    loginDisabled() {
      return process.env.loginDisabled;
    },
  },
};
</script>

<style scoped>
.product-link:not(:last-child):after {
  content: ', ';
}
.ellipsis {
  @apply absolute bottom-0 inset-x-0 h-[1.5em];
  background: linear-gradient(rgba(255, 255, 255, 0), #fff 50%);
}
.collapsed {
  @apply overflow-x-hidden;
  box-shadow: inset 0 -10px 10px -10px #fff;
}
</style>
